import { useState, useEffect, useCallback } from 'react';
import { Bell, Check, CheckCheck, Trash2, X, ExternalLink } from 'lucide-react';
import { API_URL } from '../config';

export default function Notifications({ token, onNavigate }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (e) {
      // silencieux
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.lu).length;

  const markAsRead = async (id) => {
    await fetch(`${API_URL}/notifications/${id}/lu`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` }
    });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, lu: 1 } : n));
  };

  const markAllAsRead = async () => {
    setLoading(true);
    await fetch(`${API_URL}/notifications/lu-tout`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` }
    });
    setNotifications(prev => prev.map(n => ({ ...n, lu: 1 })));
    setLoading(false);
  };

  const deleteNotification = async (id) => {
    await fetch(`${API_URL}/notifications/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleClick = async (notif) => {
    if (!notif.lu) await markAsRead(notif.id);
    if (notif.lien && onNavigate) onNavigate(notif.lien.replace('/', ''));
    setOpen(false);
  };

  const typeColors = {
    tache: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    conge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
    message: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
    planning: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700',
    demande: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
    vehicule: 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300',
    okr: 'bg-pink-100 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300',
    evenement_nouveau: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
    evenement_alert: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  };

  const typeLabels = {
    tache: 'Tâche',
    conge: 'Congé',
    message: 'Message',
    planning: 'Planning',
    demande: 'Demande',
    vehicule: 'Véhicule',
    okr: 'OKR',
    evenement_nouveau: '📅 Événement',
    evenement_alert: '⚠️ Alerte',
  };

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen(!open); if (!open) fetchNotifications(); }}
        className="relative p-2 rounded-lg hover:bg-elevated-1 transition-colors"
        title="Notifications"
      >
        <Bell size={20} className="text-text-secondary" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-10 w-96 bg-surface rounded-xl shadow-2xl border border-border-subtle z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle bg-elevated-1">
              <div className="flex items-center gap-2">
                <Bell size={16} className="text-text-secondary" />
                <span className="font-semibold text-text-primary">Notifications</span>
                {unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount} non lues
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={loading}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-200 flex items-center gap-1"
                    title="Tout marquer comme lu"
                  >
                    <CheckCheck size={14} />
                    Tout lire
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-text-muted hover:text-text-secondary">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-text-muted">
                  <Bell size={32} className="mb-2 opacity-30" />
                  <p className="text-sm">Aucune notification</p>
                </div>
              ) : (
                notifications.map(notif => (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 hover:bg-elevated-1 transition-colors ${!notif.lu ? 'bg-blue-50 dark:bg-blue-950/40' : ''}`}
                  >
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleClick(notif)}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[notif.type] || 'bg-elevated-1 text-text-secondary'}`}>
                          {typeLabels[notif.type] || notif.type}
                        </span>
                        {!notif.lu && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />}
                      </div>
                      <p className="text-sm font-medium text-text-primary truncate">{notif.titre}</p>
                      <p className="text-xs text-text-muted mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-xs text-text-muted mt-1">
                        {new Date(notif.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {!notif.lu && (
                        <button
                          onClick={() => markAsRead(notif.id)}
                          className="p-1 text-text-muted hover:text-blue-600 dark:text-blue-400 rounded"
                          title="Marquer comme lu"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      {notif.lien && (
                        <button
                          onClick={() => handleClick(notif)}
                          className="p-1 text-text-muted hover:text-green-600 dark:text-green-400 rounded"
                          title="Aller à"
                        >
                          <ExternalLink size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notif.id)}
                        className="p-1 text-text-muted hover:text-red-600 dark:text-red-400 rounded"
                        title="Supprimer"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
